import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Sparkles, 
  Users, 
  Trophy, 
  Store, 
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ideaplan-hub/artifacts/shbwt251_renovatemac_logo.png";
const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_ideaplan-hub/artifacts/2f2csoo3_renovateMac-Logo-Reverse-500x333%20%281%29.png";

const features = [
  {
    icon: Sparkles,
    title: 'AI-Powered Plans',
    description: 'Transform your business ideas into comprehensive plans with GPT-5.2 powered generation.'
  },
  {
    icon: Users,
    title: 'Community Feedback',
    description: 'Get valuable suggestions and insights from fellow entrepreneurs and experts.'
  },
  {
    icon: Trophy,
    title: 'Earn Points',
    description: 'Contribute to the community and earn points for every helpful suggestion.'
  },
  {
    icon: Store,
    title: 'Services Marketplace',
    description: 'Use your points to access marketing, accounting, design, and more services.'
  }
];

const stats = [
  { value: '10K+', label: 'Business Ideas' },
  { value: '50K+', label: 'Suggestions Made' },
  { value: '5K+', label: 'Active Members' },
  { value: '1M+', label: 'Points Exchanged' }
];

export const LandingPage = () => {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="RenovateMac" className="h-10 w-auto" />
              <span className="font-heading font-bold text-xl text-slate-900">IdeaPlan Hub</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-slate-700 hover:text-slate-900" data-testid="login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white btn-animate" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/80 text-sm mb-8 animate-fade-in">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span>Powered by AI</span>
          </div>
          
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl text-white tracking-tight mb-6 animate-fade-in stagger-1">
            Turn Ideas into
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-500">
              Business Plans
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 animate-fade-in stagger-2">
            Submit your business idea, let AI generate a comprehensive plan, and get valuable feedback from our community of entrepreneurs.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in stagger-3">
            <Link to="/register">
              <Button size="lg" className="gradient-secondary text-white text-lg px-8 py-6 btn-animate shadow-lg shadow-orange-500/25" data-testid="hero-cta-btn">
                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6" data-testid="hero-login-btn">
                Already a member?
              </Button>
            </Link>
          </div>
        </div>

        {/* Hero Image */}
        <div className="max-w-5xl mx-auto mt-16 px-4 animate-fade-in stagger-4">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/photo-1759593218431-6f1585bc14de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Entrepreneur working on business plan"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">Plan Generated</p>
                  <p className="text-slate-300 text-sm">Ready for review</p>
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2">
                <p className="font-impact text-orange-400 text-2xl">+150 PTS</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-impact text-3xl sm:text-4xl text-blue-600">{stat.value}</p>
                <p className="text-slate-600 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-blue-600 uppercase tracking-widest">Features</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mt-2">
              Everything You Need to Succeed
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="bg-white p-6 rounded-xl border border-slate-200 card-hover"
                  data-testid={`feature-card-${index}`}
                >
                  <div className={`w-12 h-12 rounded-lg ${index % 2 === 0 ? 'bg-blue-600' : 'bg-orange-500'} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-orange-500 uppercase tracking-widest">Process</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mt-2">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Submit Your Idea', desc: 'Share your business concept with our community.' },
              { step: '02', title: 'AI Generates Plan', desc: 'Our AI creates a comprehensive business plan.' },
              { step: '03', title: 'Earn & Exchange', desc: 'Get feedback, earn points, and access services.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                  <span className="font-impact text-2xl text-blue-600">{item.step}</span>
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-6">
            Ready to Build Your Business?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of entrepreneurs who are turning their ideas into actionable business plans.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-50 text-lg px-8 py-6 btn-animate" data-testid="cta-register-btn">
              Create Free Account
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={FOOTER_LOGO_URL} alt="RenovateMac" className="h-8 w-auto" />
              <span className="font-heading font-bold text-white">IdeaPlan Hub</span>
            </div>
            <p className="text-slate-400 text-sm">
              &copy; 2026 RenovateMac Marketing. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
